import * as XLSX from 'xlsx';

/**
 * Export data to an Excel file.
 */
export const exportToExcel = (data, fileName, sheetName = 'Sheet1', columns = null) => {
  let ws;
  if (columns) {
    const headers = columns.map(c => c.header);
    const rows = data.map(row => columns.map(c => row[c.key] ?? ''));
    ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = columns.map(c => ({ wch: c.width || Math.max(c.header.length + 2, 15) }));
  } else {
    ws = XLSX.utils.json_to_sheet(data);
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${fileName}.xlsx`);
};

/**
 * Export multiple sheets to a single Excel file.
 */
export const exportMultiSheet = (sheets, fileName) => {
  const wb = XLSX.utils.book_new();
  for (const sheet of sheets) {
    let ws;
    if (sheet.columns) {
      const headers = sheet.columns.map(c => c.header);
      const rows = sheet.data.map(row => sheet.columns.map(c => row[c.key] ?? ''));
      ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws['!cols'] = sheet.columns.map(c => ({ wch: c.width || Math.max(c.header.length + 2, 15) }));
    } else {
      ws = XLSX.utils.json_to_sheet(sheet.data);
    }
    // Truncate sheet name to 31 chars (Excel limit)
    const name = sheet.name.substring(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, name);
  }
  XLSX.writeFile(wb, `${fileName}.xlsx`);
};

/**
 * Sanitize sheet name for Excel (max 31 chars, no special chars)
 */
const sanitizeSheetName = (name) => {
  return name.replace(/[\\/*?[\]:]/g, '').substring(0, 31);
};

/**
 * Auto-fit column widths based on content
 */
const autoFitColumns = (data, headers) => {
  return headers.map((header, i) => {
    let maxLen = header.length;
    data.forEach(row => {
      const val = row[i] != null ? String(row[i]) : '';
      maxLen = Math.max(maxLen, val.length);
    });
    return { wch: Math.min(maxLen + 2, 50) };
  });
};

/**
 * Build an AOA sheet with a title row, blank row, then header + data rows.
 * Returns { ws, rowCount } so callers can add more sections below.
 */
const buildStyledSheet = (title, headers, rows) => {
  const aoa = [];

  // Title row
  aoa.push([title]);
  aoa.push([]); // blank row

  // Header row
  aoa.push(headers);

  // Data rows
  rows.forEach(row => aoa.push(row));

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Merge title across all header columns
  if (headers.length > 1) {
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];
  }

  // Auto-fit columns
  ws['!cols'] = autoFitColumns(rows, headers);

  return ws;
};

/**
 * Add a section (title + header + data rows) to an existing AOA at a given start row.
 */
const appendSectionToAOA = (aoa, sectionTitle, headers, rows) => {
  aoa.push([]); // blank separator
  aoa.push([sectionTitle]);
  aoa.push(headers);
  rows.forEach(row => aoa.push(row));
};

/**
 * Export detailed HR report with per-employee tabs.
 * Creates overview sheets + individual employee sheets.
 */
export const exportDetailedReport = ({ timesheetData, leaveData, visaData, headcountData }, fileName) => {
  const wb = XLSX.utils.book_new();
  const hasTimesheet = timesheetData?.employees?.length > 0;
  const hasLeave = leaveData?.details?.length > 0;
  const hasVisa = visaData?.allVisas?.length > 0;
  const hasHeadcount = headcountData?.roster?.length > 0;

  // ── 1. TIMESHEET OVERVIEW ──
  if (hasTimesheet) {
    const headers = ['Employee', 'Email', 'Job Title', 'Total Hours', 'Days Worked', 'Avg Hours/Day'];
    const rows = timesheetData.employees.map(e => [e.name, e.email, e.jobTitle, e.totalHours, e.daysWorked, e.avgHoursPerDay]);
    const ws = buildStyledSheet('Timesheet Summary', headers, rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Timesheet Overview');
  }

  // ── 2. PER-EMPLOYEE TIMESHEET TABS ──
  if (hasTimesheet && timesheetData.details?.length > 0) {
    // Group details by employee
    const byEmployee = {};
    timesheetData.details.forEach(d => {
      const key = d.employeeName;
      if (!byEmployee[key]) byEmployee[key] = [];
      byEmployee[key].push(d);
    });

    // Find matching summary for each employee
    const summaryMap = {};
    timesheetData.employees.forEach(e => { summaryMap[e.name] = e; });

    Object.entries(byEmployee).forEach(([empName, details]) => {
      const aoa = [];
      const summary = summaryMap[empName];

      // Employee header info
      aoa.push([`Timesheet: ${empName}`]);
      aoa.push([]);

      if (summary) {
        aoa.push(['Summary']);
        aoa.push(['Total Hours', summary.totalHours]);
        aoa.push(['Days Worked', summary.daysWorked]);
        aoa.push(['Avg Hours/Day', summary.avgHoursPerDay]);
        aoa.push(['Email', summary.email]);
        aoa.push(['Job Title', summary.jobTitle]);
        aoa.push([]);
      }

      // Daily breakdown header
      aoa.push(['Daily Breakdown']);
      aoa.push(['Date', 'Status', 'Total Hours', 'Task', 'Task Hours', 'Description']);

      // Each day, with tasks expanded
      details.sort((a, b) => a.date.localeCompare(b.date));
      details.forEach(day => {
        if (day.entries && day.entries.length > 0) {
          day.entries.forEach((entry, i) => {
            aoa.push([
              i === 0 ? day.date : '',
              i === 0 ? day.status : '',
              i === 0 ? day.totalHours : '',
              entry.taskName,
              entry.hours,
              entry.description || '',
            ]);
          });
        } else {
          aoa.push([day.date, day.status, day.totalHours, '—', '', '']);
        }
      });

      // Hours chart (text-based bar chart in a column)
      aoa.push([]);
      aoa.push(['Daily Hours Chart']);
      aoa.push(['Date', 'Hours', 'Visual']);
      details.forEach(day => {
        const bar = '█'.repeat(Math.round(day.totalHours));
        aoa.push([day.date, day.totalHours, bar]);
      });

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      // Merge title
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
      ws['!cols'] = [
        { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 25 }, { wch: 12 }, { wch: 35 },
      ];
      const sheetName = sanitizeSheetName(`TS - ${empName}`);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });
  }

  // ── 3. LEAVE OVERVIEW ──
  if (hasLeave) {
    const aoa = [];
    aoa.push(['Leave Report']);
    aoa.push([]);

    // By Type summary
    if (leaveData.byType?.length > 0) {
      aoa.push(['Leave by Type']);
      aoa.push(['Type', 'Count', 'Visual']);
      leaveData.byType.forEach(t => {
        aoa.push([t.type, t.count, '█'.repeat(t.count)]);
      });
      aoa.push([]);
    }

    // By Month summary
    if (leaveData.byMonth?.length > 0) {
      aoa.push(['Leave by Month']);
      aoa.push(['Month', 'Count', 'Visual']);
      leaveData.byMonth.forEach(m => {
        aoa.push([m.month, m.count, '█'.repeat(m.count)]);
      });
      aoa.push([]);
    }

    // Per-employee summary
    if (leaveData.byEmployee?.length > 0) {
      aoa.push(['Per-Employee Summary']);
      aoa.push(['Employee', 'Email', 'Total Requests', 'Total Days', 'Breakdown']);
      leaveData.byEmployee.forEach(e => {
        aoa.push([e.employeeName, e.email, e.totalRequests, e.totalDays, e.breakdown]);
      });
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
    ws['!cols'] = [{ wch: 22 }, { wch: 28 }, { wch: 16 }, { wch: 12 }, { wch: 35 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Leave Overview');
  }

  // ── 4. PER-EMPLOYEE LEAVE TABS ──
  if (hasLeave) {
    const byEmp = {};
    leaveData.details.forEach(d => {
      const key = d.employeeName;
      if (!byEmp[key]) byEmp[key] = [];
      byEmp[key].push(d);
    });

    const empSummaryMap = {};
    (leaveData.byEmployee || []).forEach(e => { empSummaryMap[e.employeeName] = e; });

    Object.entries(byEmp).forEach(([empName, records]) => {
      const aoa = [];
      const summary = empSummaryMap[empName];

      aoa.push([`Leave: ${empName}`]);
      aoa.push([]);

      if (summary) {
        aoa.push(['Summary']);
        aoa.push(['Total Requests', summary.totalRequests]);
        aoa.push(['Total Days', summary.totalDays]);
        aoa.push(['Breakdown', summary.breakdown]);
        aoa.push(['Email', summary.email]);
        aoa.push([]);

        // Mini chart of days by type
        if (summary.byType) {
          aoa.push(['Days by Type']);
          aoa.push(['Type', 'Days', 'Visual']);
          Object.entries(summary.byType).forEach(([type, days]) => {
            aoa.push([type, days, '█'.repeat(Math.round(days))]);
          });
          aoa.push([]);
        }
      }

      aoa.push(['All Leave Records']);
      aoa.push(['Type', 'Start Date', 'End Date', 'Days', 'Reason', 'Status']);
      records.forEach(r => {
        aoa.push([r.leaveType, r.startDate, r.endDate, r.daysCount, r.reason, r.status]);
      });

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
      ws['!cols'] = [
        { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 8 }, { wch: 35 }, { wch: 12 },
      ];
      const sheetName = sanitizeSheetName(`LV - ${empName}`);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });
  }

  // ── 5. VISA OVERVIEW ──
  if (hasVisa) {
    const aoa = [];
    aoa.push(['Visa Report']);
    aoa.push([]);

    // Status summary with chart
    if (visaData.statusCounts) {
      aoa.push(['Status Summary']);
      aoa.push(['Status', 'Count', 'Visual']);
      const sc = visaData.statusCounts;
      if (sc.active) aoa.push(['Active', sc.active, '█'.repeat(sc.active)]);
      if (sc.expiring_soon) aoa.push(['Expiring Soon', sc.expiring_soon, '█'.repeat(sc.expiring_soon)]);
      if (sc.expired) aoa.push(['Expired', sc.expired, '█'.repeat(sc.expired)]);
      aoa.push([]);
    }

    // Full visa table
    aoa.push(['All Visa Details']);
    aoa.push(['Employee', 'Email', 'Job Title', 'Visa Type', 'Visa Number', 'Issue Date', 'Expiry Date', 'Status', 'Country', 'Notes']);
    visaData.allVisas.forEach(v => {
      aoa.push([v.employeeName, v.email, v.jobTitle, v.visaType, v.visaNumber || '', v.issueDate || '', v.expiryDate, v.status, v.countryOfOrigin, v.notes || '']);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 9 } }];
    ws['!cols'] = [
      { wch: 22 }, { wch: 28 }, { wch: 20 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 30 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Visa Overview');

    // Per-employee visa tabs
    visaData.allVisas.forEach(v => {
      const aoa = [];
      aoa.push([`Visa: ${v.employeeName}`]);
      aoa.push([]);
      aoa.push(['Field', 'Value']);
      aoa.push(['Employee', v.employeeName]);
      aoa.push(['Email', v.email]);
      aoa.push(['Job Title', v.jobTitle]);
      aoa.push(['Visa Type', v.visaType]);
      aoa.push(['Visa Number', v.visaNumber || '—']);
      aoa.push(['Issue Date', v.issueDate || '—']);
      aoa.push(['Expiry Date', v.expiryDate]);
      aoa.push(['Status', v.status]);
      aoa.push(['Country of Origin', v.countryOfOrigin]);
      aoa.push(['Notes', v.notes || '—']);

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
      ws['!cols'] = [{ wch: 20 }, { wch: 40 }];
      const sheetName = sanitizeSheetName(`VS - ${v.employeeName}`);
      // Avoid duplicate sheet names
      let finalName = sheetName;
      let counter = 2;
      while (wb.SheetNames.includes(finalName)) {
        finalName = sanitizeSheetName(`VS${counter} - ${v.employeeName}`);
        counter++;
      }
      XLSX.utils.book_append_sheet(wb, ws, finalName);
    });
  }

  // ── 6. HEADCOUNT OVERVIEW ──
  if (hasHeadcount) {
    const aoa = [];
    aoa.push(['Headcount Report']);
    aoa.push([]);

    // By department chart
    if (headcountData.byDepartment?.length > 0) {
      aoa.push(['By Department']);
      aoa.push(['Department', 'Count', 'Visual']);
      headcountData.byDepartment.forEach(d => {
        aoa.push([d.departmentName, d.count, '█'.repeat(d.count)]);
      });
      aoa.push([]);
    }

    // By employment type chart
    if (headcountData.byType?.length > 0) {
      aoa.push(['By Employment Type']);
      aoa.push(['Type', 'Count', 'Visual']);
      headcountData.byType.forEach(t => {
        aoa.push([t.employmentType, t.count, '█'.repeat(t.count)]);
      });
      aoa.push([]);
    }

    // Full roster
    aoa.push(['Full Employee Roster']);
    aoa.push(['Name', 'Email', 'Department', 'Job Title', 'Employment Type', 'Join Date']);
    headcountData.roster.forEach(r => {
      aoa.push([r.name, r.email, r.department, r.jobTitle, r.employmentType, r.joinDate || '']);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
    ws['!cols'] = [
      { wch: 22 }, { wch: 28 }, { wch: 18 }, { wch: 22 }, { wch: 18 }, { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Headcount Overview');
  }

  if (wb.SheetNames.length === 0) {
    const ws = XLSX.utils.aoa_to_sheet([['No data loaded. Visit each report tab first.']]);
    XLSX.utils.book_append_sheet(wb, ws, 'Empty');
  }

  XLSX.writeFile(wb, `${fileName}.xlsx`);
};
